import type { HTTPClient, APIOptions } from '../core/http-client.js';
import { API_ENDPOINTS } from '../core/constants.js';
import type { Recipient, SendMessageRequest, MessagingType } from '../types/messages.js';
import type { SendMessageResponse } from '../types/responses.js';
import { validateGenericTemplate, validateButtonTemplate, validateMediaTemplate } from '../utils/validators.js';
import type {
  GenericTemplatePayload,
  ButtonTemplatePayload,
  MediaTemplatePayload,
  ProductTemplatePayload,
  GenericTemplateElement,
  Button,
  MediaTemplateElement,
  ProductTemplateElement,
} from '../types/templates.js';

export class TemplatesAPI {
  constructor(private httpClient: HTTPClient) {}

  async generic(options: {
    recipient: Recipient;
    elements: GenericTemplateElement[];
    messaging_type?: MessagingType;
    image_aspect_ratio?: 'horizontal' | 'square';
    notification_type?: 'REGULAR' | 'SILENT_PUSH' | 'NO_PUSH';
    tag?: string;
  }, apiOptions?: APIOptions): Promise<SendMessageResponse> {
    // Validate template
    validateGenericTemplate(options.elements);

    const payload: GenericTemplatePayload = {
      template_type: 'generic',
      elements: options.elements,
      image_aspect_ratio: options.image_aspect_ratio,
    };

    const request: SendMessageRequest = {
      recipient: options.recipient,
      messaging_type: options.messaging_type || 'UPDATE',
      message: {
        attachment: {
          type: 'template',
          payload,
        },
      },
      notification_type: options.notification_type,
      tag: options.tag,
    };

    return this.httpClient.request<SendMessageResponse>({
      method: 'POST',
      path: API_ENDPOINTS.MESSAGES,
      body: request,
      accessToken: apiOptions?.accessToken,
    });
  }

  async button(options: {
    recipient: Recipient;
    text: string;
    buttons: Button[];
    messaging_type?: MessagingType;
    notification_type?: 'REGULAR' | 'SILENT_PUSH' | 'NO_PUSH';
    tag?: string;
  }, apiOptions?: APIOptions): Promise<SendMessageResponse> {
    // Validate template
    validateButtonTemplate(options.text, options.buttons);

    const payload: ButtonTemplatePayload = {
      template_type: 'button',
      text: options.text,
      buttons: options.buttons,
    };

    const request: SendMessageRequest = {
      recipient: options.recipient,
      messaging_type: options.messaging_type || 'UPDATE',
      message: {
        attachment: {
          type: 'template',
          payload,
        },
      },
      notification_type: options.notification_type,
      tag: options.tag,
    };

    return this.httpClient.request<SendMessageResponse>({
      method: 'POST',
      path: API_ENDPOINTS.MESSAGES,
      body: request,
      accessToken: apiOptions?.accessToken,
    });
  }

  async media(options: {
    recipient: Recipient;
    element: MediaTemplateElement;
    messaging_type?: MessagingType;
    notification_type?: 'REGULAR' | 'SILENT_PUSH' | 'NO_PUSH';
    tag?: string;
  }, apiOptions?: APIOptions): Promise<SendMessageResponse> {
    // Validate template
    validateMediaTemplate(options.element);

    const payload: MediaTemplatePayload = {
      template_type: 'media',
      elements: [options.element],
    };

    const request: SendMessageRequest = {
      recipient: options.recipient,
      messaging_type: options.messaging_type || 'UPDATE',
      message: {
        attachment: {
          type: 'template',
          payload,
        },
      },
      notification_type: options.notification_type,
      tag: options.tag,
    };

    return this.httpClient.request<SendMessageResponse>({
      method: 'POST',
      path: API_ENDPOINTS.MESSAGES,
      body: request,
      accessToken: apiOptions?.accessToken,
    });
  }

  async product(options: {
    recipient: Recipient;
    elements: ProductTemplateElement[];
    messaging_type?: MessagingType;
    notification_type?: 'REGULAR' | 'SILENT_PUSH' | 'NO_PUSH';
    tag?: string;
  }, apiOptions?: APIOptions): Promise<SendMessageResponse> {
    const payload: ProductTemplatePayload = {
      template_type: 'product',
      elements: options.elements,
    };

    const request: SendMessageRequest = {
      recipient: options.recipient,
      messaging_type: options.messaging_type || 'UPDATE',
      message: {
        attachment: {
          type: 'template',
          payload,
        },
      },
      notification_type: options.notification_type,
      tag: options.tag,
    };

    return this.httpClient.request<SendMessageResponse>({
      method: 'POST',
      path: API_ENDPOINTS.MESSAGES,
      body: request,
      accessToken: apiOptions?.accessToken,
    });
  }
}